
import { NSCommons } from '@ruby/configs/common.constants';
import { NSMapConstants } from '@ruby/configs/map.constants';
import { EMapSymbol } from '@ruby/shared/enums/map.enums';
import { IMapConfig } from '@ruby/shared/models/map/map.interface';
import { IShipment, IShipmentAttributes } from '@ruby/shared/models/shipments/shipments.interface';

import esri = __esri;

export class NSCTMSMapConstants {
  static readonly config: IMapConfig = {
    show: {
      expand: false,
      filter: false,
      clearRoute: true
    },
    disable: {
      clearRoute: true
    },
    fieldDef: [
      { name: 'carQty', type: 'double' },
      { name: 'carsWb', type: 'double' },
      { name: 'crewCall', type: 'double' },
      { name: 'destination', type: 'string' },
      { name: 'eta', type: 'string' },
      { name: 'eventTime', type: 'date' },
      { name: 'eventType', type: 'string' },
      { name: 'leInd', type: 'string' },
      { name: 'location', type: 'string' },
      { name: 'origin', type: 'string' },
      { name: 'permitId', type: 'double' },
      { name: 'permitNumber', type: 'string' },
      { name: 'shipmentId', type: 'double' },
      { name: 'traceCar', type: 'string' },
      { name: 'trainId', type: 'string' },
      { name: 'trainNumber', type: 'double' }
    ],
    fieldInfos: [
      { fieldName: 'leInd', label: 'L/E' },
      { fieldName: 'location', label: 'Location' },
      { fieldName: 'eventType', label: 'Event Type' },
      { fieldName: 'eventTime', label: 'Event Date' },
      { fieldName: 'trainId', label: 'Train Id' },
      { fieldName: 'eta', label: 'ETA' },
      { fieldName: 'crewCall', label: 'Crew Call' },
      { fieldName: 'carQty', label: 'Cars' },
      { fieldName: 'carsWb', label: '#WB/Con' },
      { fieldName: 'origin', label: 'Origin' },
      { fieldName: 'destination', label: 'Destination' },
      { fieldName: 'traceCar', label: 'Trace Car' }
    ]
  };

  static getPopupTemplate(type: EMapSymbol): esri.PopupTemplateProperties {
    const popup: esri.PopupTemplateProperties = {
      content: NSCTMSMapConstants.getTemplate,
      fieldInfos: NSCTMSMapConstants.config.fieldInfos,
      outFields: ['*']
    };
    switch (type) {
      case EMapSymbol.START:
        popup.title = '{stationName}, {state} (Start)';
        break;
      case EMapSymbol.END:
        popup.title = '{stationName}, {state} (Destination)';
        break;
      case EMapSymbol.CHECKPOINT:
        popup.title = '{stationName}, {state}';
        break;
      case EMapSymbol.POINT:
        popup.title = '{permitNumber}';
        popup.content = [
          {
            type: 'fields',
            fieldInfos: NSCTMSMapConstants.config.fieldInfos
          }
        ];
        popup.actions = [
          {
            title: 'View Route',
            id: 'routeRef',
            className: 'esri-icon-polyline'
          },
          {
            title: 'View Permit',
            id: 'permitRef',
            className: 'esri-icon-zoom-out-fixed'
          },
          {
            title: 'Train Consist',
            className: 'esri-icon-organization',
            id: 'trainRef'
          }
        ] as Array<esri.ActionButtonProperties & { type: 'button' }>;
    }
    return popup;
  }

  static async getTemplate(param: { graphic: IShipment }): Promise<HTMLElement> {
    const container: HTMLElement = document.createElement('div');
    const attributes: IShipmentAttributes = param.graphic.attributes;
    let elements = '';
    if (attributes?.operatingStationNumber) {
      elements += NSCTMSMapConstants.getRow('Operating Station', attributes.operatingStationNumber);
    }
    param.graphic.attributes?.events?.forEach((value: { eventDate: number; eventType: string }) => {
      elements += NSCTMSMapConstants.getRow('Event Type', value.eventType, true);
      elements += NSCTMSMapConstants.getRow('Event Date', NSCommons.formatNSDate(value.eventDate));
    });
    if (attributes?.dWell) {
      elements += NSCTMSMapConstants.getRow('Dwell (Hrs)', attributes.dWell);
    }
    container.className = 'd-flex flex-column';
    container.innerHTML = elements;
    return container;
  }

  static getRow(key: string, value: string | number, padding?: boolean): string {
    return `<div class="row m-0 ${ padding ? 'pt-2' : '' }">
                <div class="col"><b>${ key }:</b></div>
                <div class="col">${ value }</div>
            </div>`;
  }

  static getFeatureLayerProperties(graphics: Array<esri.Graphic>): esri.FeatureLayerProperties {
    return {
      id: 'ctms-feature-layer',
      objectIdField: 'OBJECTID',
      outFields: ['*'],
      copyright: 'Norfolk Southern',
      source: graphics,
      popupTemplate: NSCTMSMapConstants.getPopupTemplate(EMapSymbol.POINT),
      fields: NSCTMSMapConstants.config.fieldDef,
      featureReduction: {
        type: 'cluster',
        popupTemplate: {
          title: 'Permits summary',
          content: 'This cluster represents {cluster_count} permits.',
          fieldInfos: [{
            fieldName: 'cluster_count',
            format: {
              places: 0,
              digitSeparator: true
            }
          }]
        },
        clusterMinSize: '20px',
        clusterMaxSize: '40px',
        labelingInfo: NSMapConstants.getLabelingInfo(EMapSymbol.CLUSTER)
      },
      labelingInfo: NSMapConstants.getLabelingInfo(EMapSymbol.POINT),
      renderer: NSMapConstants.pointRenderer
    };
  }
}
